import {deleteField, FieldValue} from 'firebase/firestore';

import type {SectionDocument} from '@cip/core/firebase';
import type {SectionSignupV2SlotAvailability} from '@cip/core/firebase/client';
import {MAX_CATCH_UP_WEEKS, type SectionWeekCreditInput} from '@cip/core/section';

import type {CourseUser} from 'contexts/ProfileContext';
import {isMinimumRole} from 'contexts/profileUtil';
import type {CourseId} from 'types/common';
import {Role} from 'types/role';

/** The made-up students staff can view a course as. */
export const STAFF_VIEW_AS_KINDS = [
  'student',
  'minorStudent',
  'studentWithTime',
  'seatedStudent',
  'superSectionStudent',
  'usedCatchUpsStudent',
  'sectionLeader',
] as const;

/**
 * `'student'`: an adult student with no section yet. `'minorStudent'`: the
 * same student, under 18. `'studentWithTime'`: the adult student, who has
 * already picked a section time and a backup in onboarding.
 * `'seatedStudent'`: an adult with a seat in a small section of the course
 * ({@link StaffViewAs.seat}). `'superSectionStudent'`: an adult placed in the
 * Super Section. `'usedCatchUpsStudent'`: a seated student who has counted
 * every catch-up the course allows. `'sectionLeader'`: a confirmed section
 * leader with no section yet.
 */
export type StaffViewAsKind = (typeof STAFF_VIEW_AS_KINDS)[number];

/** A small section a made-up student has a seat in. */
export interface StaffViewAsSeat {
  /** The section's id. */
  sectionId: string;
  /** Its weekly time. */
  timeIndex: number;
}

/** A staff member viewing one course as a made-up student. */
export interface StaffViewAs {
  /** The course the view applies to; other courses show the staff member. */
  courseId: CourseId;
  /** The student being viewed as. */
  kind: StaffViewAsKind;
  /**
   * The made-up student's `studentonboarding` doc, held in memory only, so
   * onboarding and the section page agree on what the student did.
   */
  onboardingData: Record<string, unknown>;
  /**
   * The made-up student's small section, which only the catch-up page reads;
   * absent for a student without one.
   */
  seat?: StaffViewAsSeat;
  /**
   * The section week (one-based) the catch-up page shows; absent shows the
   * weeks that have met, as for a real student.
   */
  sectionWeek?: number;
}

/**
 * The view that applies to the current course. Only staff (TA and above) by
 * their real role can view as a student.
 *
 * @param params.viewAs - The stored view, or `null` for none.
 * @param params.courseId - The current course.
 * @param params.realCourseRole - The signed-in user's own role in the course.
 * @return The view, or `null` when there is none for this course or the user
 *     is not staff.
 * @throws Never.
 */
export function getActiveStaffViewAs({
  viewAs,
  courseId,
  realCourseRole,
}: {
  viewAs: StaffViewAs | null;
  courseId: CourseId;
  realCourseRole: Role;
}): StaffViewAs | null {
  if (viewAs === null || viewAs.courseId !== courseId) {
    return null;
  }
  return isMinimumRole(realCourseRole, Role.TA) ? viewAs : null;
}

/** The profile fields a view replaces. */
type StaffViewAsFields = Pick<
  CourseUser,
  | 'courseRole'
  | 'isMinor'
  | 'courseSections'
  | 'sections'
  | 'confirmedStudent'
  | 'confirmedSectionLeader'
>;

/**
 * Whether a view is of a student, so student onboarding runs as its
 * in-memory preview for it.
 *
 * @param kind - The view's kind.
 * @return `false` only for the section leader view.
 * @throws Never.
 */
export function isStudentViewAsKind(kind: StaffViewAsKind): boolean {
  return kind !== 'sectionLeader';
}

/**
 * The profile pages see while staff view the course as a made-up person: a
 * confirmed student with no section in the course, under 18 for
 * `'minorStudent'`, or for `'sectionLeader'` a confirmed section leader with
 * no section. Confirmed, so SL onboarding offers "Enter Course" and never
 * the "Generate Section" call that would act on the staff member's account.
 * Everything else stays the staff member's own, including the id the server
 * sees.
 *
 * @param params.userData - The staff member's own profile.
 * @param params.courseId - The course being viewed.
 * @param params.kind - The student being viewed as.
 * @return A new profile; `userData` is not changed.
 * @throws Never.
 */
export function applyStaffViewAs<T extends StaffViewAsFields>({
  userData,
  courseId,
  kind,
}: {
  userData: T;
  courseId: CourseId;
  kind: StaffViewAsKind;
}): T {
  if (kind === 'sectionLeader') {
    return {
      ...userData,
      courseRole: Role.SECTION_LEADER,
      isMinor: false,
      courseSections: [],
      sections: {...userData.sections, [courseId]: []},
      confirmedSectionLeader: {
        ...userData.confirmedSectionLeader,
        [courseId]: true,
      },
    };
  }
  return {
    ...userData,
    courseRole: Role.STUDENT,
    isMinor: kind === 'minorStudent',
    courseSections: [],
    sections: {...userData.sections, [courseId]: []},
    confirmedStudent: {...userData.confirmedStudent, [courseId]: true},
  };
}

/**
 * Onboarding picks for the `'studentWithTime'` view: the main-pool time
 * with the most sections as the first choice, and the next as the backup.
 * Ties go to the earlier time.
 *
 * @param slots - Availability slots, of either pool.
 * @return The onboarding fields; empty when no main time has a section,
 *     and without a backup when only one does.
 * @throws Never.
 */
export function pickSampleSectionTimes(
  slots: readonly SectionSignupV2SlotAvailability[],
): Record<string, unknown> {
  const [first, second] = slots
    .filter(slot => slot.type === 'main' && slot.numSections > 0)
    .sort((a, b) => b.numSections - a.numSections || a.timeIndex - b.timeIndex);
  if (first === undefined) {
    return {};
  }
  return second === undefined
    ? {sectionTimeIndex: first.timeIndex}
    : {
        sectionTimeIndex: first.timeIndex,
        backupSectionTimeIndex: second.timeIndex,
      };
}

/** A section document with its id, as {@link pickSampleSeat} reads it. */
type SectionWithId = Pick<
  SectionDocument,
  'timeIndex' | 'isSuperSection' | 'isTestSection' | 'isMinor'
> & {id: string};

/**
 * A seat for the made-up seated students: the adult small section that
 * meets earliest in the week. Ties go to the lower id.
 *
 * @param params.sections - Sections of the course.
 * @param params.superSectionIds - The course's Super Sections.
 * @return The seat, or `null` when no section qualifies.
 * @throws Never.
 */
export function pickSampleSeat({
  sections,
  superSectionIds,
}: {
  sections: readonly SectionWithId[];
  superSectionIds: readonly string[];
}): StaffViewAsSeat | null {
  const seats = sections.flatMap(section =>
    typeof section.timeIndex === 'number' &&
    section.isSuperSection !== true &&
    section.isTestSection !== true &&
    section.isMinor !== true &&
    !superSectionIds.includes(section.id)
      ? [{sectionId: section.id, timeIndex: section.timeIndex}]
      : [],
  );
  seats.sort(
    (a, b) =>
      a.timeIndex - b.timeIndex || a.sectionId.localeCompare(b.sectionId),
  );
  return seats[0] ?? null;
}

/**
 * The credit input of a made-up student who counted every catch-up the
 * course allows: the earliest weeks other than `avoidWeekIndex` get a
 * recording of `sectionId` marked watched and the exercise done, so the
 * avoided week shows the over-limit state.
 *
 * @param params.input - The student's real input, usually empty.
 * @param params.sectionId - The small section whose recordings were marked.
 * @param params.avoidWeekIndex - A zero-based week to leave untouched;
 *     `null` touches the earliest weeks.
 * @return A new input; `input` is not changed.
 * @throws Never.
 */
export function simulateUsedCatchUps({
  input,
  sectionId,
  avoidWeekIndex,
}: {
  input: SectionWeekCreditInput;
  sectionId: string;
  avoidWeekIndex: number | null;
}): SectionWeekCreditInput {
  const weeks: number[] = [];
  for (let week = 0; weeks.length < MAX_CATCH_UP_WEEKS; week++) {
    if (week !== avoidWeekIndex) {
      weeks.push(week);
    }
  }
  const keys = weeks.map(String);
  return {
    ...input,
    sectionRecordingAttendance: {
      ...input.sectionRecordingAttendance,
      ...Object.fromEntries(keys.map(key => [key, true])),
    },
    sectionRecordingAttendanceIds: {
      ...input.sectionRecordingAttendanceIds,
      ...Object.fromEntries(keys.map(key => [key, [sectionId]])),
    },
    exerciseWeeks: [...new Set([...input.exerciseWeeks, ...weeks])],
    completedExerciseWeeks: [
      ...new Set([...input.completedExerciseWeeks, ...weeks]),
    ],
  };
}

const DELETE_FIELD = deleteField();

/**
 * Merges fields into an in-memory onboarding doc the way `setDoc` with
 * `merge` would, removing fields set to `deleteField()`.
 *
 * @param params.onboardingData - The doc before the write.
 * @param params.fields - The fields written.
 * @return The doc after the write.
 * @throws Never.
 */
export function mergeInMemoryOnboarding({
  onboardingData,
  fields,
}: {
  onboardingData: Record<string, unknown>;
  fields: Record<string, unknown>;
}): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries({...onboardingData, ...fields}).filter(
      ([, value]) =>
        !(value instanceof FieldValue && value.isEqual(DELETE_FIELD)),
    ),
  );
}
